import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import TableContainer from '@mui/material/TableContainer';
import HelpIcon from '@mui/icons-material/Help';
import IconButton from '@mui/material/IconButton';
import { Table, getPaginationLabels } from '@components/Table';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import { ModalConfirm } from '@components/Modal';
import SplitButton from '@components/SplitButton';
import LockedButton from '@features/LockedButton';
import PageHeader from '@features/PageHeader';
import { companyService } from '@services';
import { accountActions, alertActions, authActions } from '@actions';
import { useTranslation, usePermissions } from '@hooks';
import { getCompanyAddress } from '@helpers/companies';
import { COUNTRY_NAMES } from '@constants/countries';
import { PRICING_DRAWER_TYPE } from '@constants/pricing-drawer-ui';
import { COMPANY_STATUS, COMPLETENESS_LEVEL_TRANSLATION_KEY, resolveCompletenessLevel } from '@constants/companies';
import { LocationIntegrationName, AccIntegrationName } from '@constants/integrations';
import { isIntegrationConnected } from '@helpers/integrations';
import { handleApiError } from '@helpers/api';
import PageContainer from '@components/Layout/PageContainer';
import { LocationsSeoIcon } from 'assets/images/icons';
import {
    GoogleIcon,
    IFoodIcon,
    CoverManagerIcon,
    FacebookIcon,
    VTEXIcon,
    SquareIcon,
    ListingDirectoriesIcon,
    XIcon,
    tiktokIcon,
} from 'assets/images/integrations';
import ImportGoogleCompanies from '../ImportGoogleCompanies';
import AddLocationModal from '../AddLocationModal';
import UpdateLocations from '../UpdateLocations';
import BulkEditLocations from '../BulkEditLocations';
import ProfileProgress from '@components/ProfileProgress';
import FiltersPanel from '../FiltersPanel';

const StyledLockedButton = styled(LockedButton)({
    marginRight: 8,
    '& svg': {
        width: 16,
        height: 14,
    },
});

const StyledSplitButton = styled(SplitButton)({
    '& svg': {
        width: 24,
        height: 24,
    },
});

const IconsWrapper = styled('span')({
    display: 'flex',
    height: '18px',
    gap: '8px',
    '& svg': {
        height: 18,
        width: 'auto',
    },
});

const GetCompaniesColumnNames = canEdit => {
    const { t } = useTranslation();
    const columns = [
        { key: 0, width: 240, component: <Typography variant="subtitle2">{t('common.location')}</Typography> },
        {
            key: 1,
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('SettingsCompanies.address')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.brand')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.status')}
                </Typography>
            ),
        },
        {
            key: 4,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('SettingsCompanies.integrations')}
                </Typography>
            ),
        },
        {
            key: 5,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('SettingsCompanies.optimizationScore')}
                    <IconButton sx={{ padding: '4px !important', marginLeft: '4px' }}>
                        <Tooltip
                            title={t('SettingsCompanies.optimizationScoreTooltip')}
                            placement="top"
                            enterTouchDelay={0}
                        >
                            <HelpIcon sx={{ height: '18px !important', width: '18px !important' }} />
                        </Tooltip>
                    </IconButton>
                </Typography>
            ),
        },
    ];
    if (canEdit) {
        columns.push({ key: 6, component: <Typography align="left" variant="subtitle2"></Typography> });
    }
    return columns;
};

const LocationIntegrationIcon = ({ type }) => {
    switch (type) {
        case LocationIntegrationName.Google:
            return <GoogleIcon />;
        case LocationIntegrationName.Facebook:
            return <FacebookIcon />;
        case LocationIntegrationName.iFood:
            return <IFoodIcon />;
        case LocationIntegrationName.CoverManager:
            return <CoverManagerIcon />;
        case LocationIntegrationName.VTEX:
            return <VTEXIcon />;
        case LocationIntegrationName.ListingDirectories:
            return <ListingDirectoriesIcon />;
        case LocationIntegrationName.Tiktok:
            return <img src={tiktokIcon} alt="TikTok" style={{ height: 18, width: 'auto' }} />;
        case LocationIntegrationName.X:
            return <XIcon />;
        case LocationIntegrationName.Square:
            return <SquareIcon />;
        default:
            return null;
    }
};

// Order of integrations to match IntegrationsTable
const INTEGRATION_ORDER = [
    LocationIntegrationName.Google,
    LocationIntegrationName.Facebook,
    LocationIntegrationName.ListingDirectories,
    LocationIntegrationName.CoverManager,
    LocationIntegrationName.iFood,
    LocationIntegrationName.VTEX,
    LocationIntegrationName.Tiktok,
    LocationIntegrationName.X,
    LocationIntegrationName.Square,
];

const getRowsData = (companies, canEditAny, hasFullAccessAt) => {
    const { t } = useTranslation();
    return companies.map(company => {
        const fullAddress =
            (company.hasBusinessAddress && getCompanyAddress(company.addressLine1, company.city)) ||
            COUNTRY_NAMES[company.country] ||
            '--';
        const integrationsConnected = company.integrations
            .map(i => i.type)
            .sort((a, b) => INTEGRATION_ORDER.indexOf(a) - INTEGRATION_ORDER.indexOf(b));
        const canEditRow = hasFullAccessAt('locations', company.companyId);
        const cells = [
            {
                component: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <Typography variant="subtitle2" align="left">
                            {company.internalName}
                        </Typography>
                    </span>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {fullAddress}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {company.brand?.title || '--'}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography
                        variant="body2"
                        align="left"
                        noWrap
                        sx={{
                            maxWidth: '150px',
                            color: company.status === COMPANY_STATUS.OPEN ? 'var(--text-primary)' : 'var(--error-main)',
                        }}
                    >
                        {company.status === COMPANY_STATUS.OPEN ? t('common.open') : t('common.closed')}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {integrationsConnected.length ? (
                            <IconsWrapper>
                                {integrationsConnected.map(type => (
                                    <LocationIntegrationIcon key={type} type={type} />
                                ))}
                            </IconsWrapper>
                        ) : (
                            t('common.none')
                        )}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <ProfileProgress value={company?.completeness} level={resolveCompletenessLevel(company)} />{' '}
                            {t(COMPLETENESS_LEVEL_TRANSLATION_KEY[resolveCompletenessLevel(company)])}
                        </span>
                    </Typography>
                ),
            },
        ];
        if (canEditAny) {
            cells.push(canEditRow ? { value: company } : {});
        }
        return cells;
    });
};

const CompaniesTable = ({
    filter,
    setFilter,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    account,
    view,
    setView,
    selectedCompany,
    setSelectedCompany,
    openLocationEdit,
}) => {
    const { t } = useTranslation();
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);
    const [isUpdateLocationsOpen, setIsUpdateLocationsOpen] = useState(false);
    const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
    const [isImportGoogleCompaniesOpen, setIsImportGoogleCompaniesOpen] = useState(false);

    const { locations, locationsLoading, totalCount, pages } = useSelector(state => state.account);
    const dispatch = useDispatch();
    const { hasFullAccess, hasFullAccessAt, hasGlobalScope, hasSettingsAccess } = usePermissions();

    const canEdit = hasFullAccess('locations');
    const canCreate = canEdit && hasGlobalScope;
    const GOOGLE_CONNECTED = isIntegrationConnected(account, AccIntegrationName.Google);

    const onDelete = async () => {
        try {
            await companyService.deleteCompany(selectedCompany?.companyId);
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            dispatch(accountActions.get());
            dispatch(authActions.getCurrentUser());
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail', { amount: error.count });
        }
        setDeleteModalOpen(false);
        setSelectedCompany({});
    };

    const onPageChange = (_, newPage) => {
        setPage(newPage);
        dispatch(accountActions.getLocations(filter, newPage, rowsPerPage));
    };

    const onRowsPerPageChange = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        dispatch(accountActions.getLocations(filter, 0, parseInt(event.target.value, 10)));
    };

    const onApplyFilters = nextFilter => {
        setFilter(nextFilter);
        setPage(0);
        dispatch(accountActions.getLocations(nextFilter, 0, rowsPerPage));
    };

    const limitExceeded = account?.companies?.length >= account?.companiesMaxCount;

    return (
        <PageContainer>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<LocationsSeoIcon />}
                title={t('SettingsCompanies.title')}
                subtitle={t('SettingsCompanies.subtitle')}
                metrics={
                    canEdit && (
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'var(--text-secondary)',
                                '@media (max-width: 700px)': {
                                    textAlign: 'center',
                                },
                            }}
                        >
                            {t('common.locations')}{' '}
                            <span style={{ color: 'var(--text-primary)' }}>
                                {hasSettingsAccess() && account?.companiesMaxCount !== Infinity
                                    ? t('common.outOf', {
                                          total: account?.companies?.length,
                                          max: account?.companiesMaxCount,
                                      })
                                    : account?.companies?.length}
                            </span>
                        </Typography>
                    )
                }
            >
                {canCreate ? (
                    limitExceeded || account?.billingDiffInSeconds < 0 ? (
                        <StyledLockedButton tooltipType={1} variant="contained" drawerType={PRICING_DRAWER_TYPE.CHANGE}>
                            {t(`buttons.${GOOGLE_CONNECTED ? 'importFromGoogle' : 'addLocation'}`)}
                        </StyledLockedButton>
                    ) : (
                        <StyledSplitButton
                            variant="contained"
                            options={
                                GOOGLE_CONNECTED
                                    ? [
                                          {
                                              text: t('buttons.importFromGoogle'),
                                              onClick: () => setIsImportGoogleCompaniesOpen(true),
                                              disabled: !GOOGLE_CONNECTED,
                                          },
                                          {
                                              text: t('buttons.addLocationManually'),
                                              onClick: () => setIsAddLocationOpen(true),
                                          },
                                      ]
                                    : [
                                          {
                                              text: t('buttons.addLocationManually'),
                                              onClick: () => setIsAddLocationOpen(true),
                                          },
                                          {
                                              text: t('buttons.importFromGoogle'),
                                              onClick: () => setIsImportGoogleCompaniesOpen(true),
                                              disabled: !GOOGLE_CONNECTED,
                                          },
                                      ]
                            }
                        />
                    )
                ) : null}
                {canEdit && (
                    <Button variant="outlined" onClick={() => setIsBulkEditOpen(true)}>
                        {t('buttons.bulkEdit')}
                    </Button>
                )}
                {canCreate && (
                    <Button variant="outlined" onClick={() => setIsUpdateLocationsOpen(true)}>
                        {t('buttons.bulkUpdateCSV')}
                    </Button>
                )}
                <FiltersPanel onApplyFilters={onApplyFilters} filter={filter} />
            </PageHeader>
            <TableContainer>
                <Table
                    columnNames={GetCompaniesColumnNames(canEdit)}
                    rows={getRowsData(locations, canEdit, hasFullAccessAt)}
                    isDataLoading={locationsLoading}
                    onPrimaryAction={canEdit ? ({ value }) => openLocationEdit(value) : undefined}
                    onSecondaryAction={
                        canEdit
                            ? ({ value }) => {
                                  setSelectedCompany(value);
                                  setDeleteModalOpen(true);
                              }
                            : undefined
                    }
                    isInteractive={canEdit}
                    withPagination
                    paginationProps={{
                        colSpan: 7,
                        count: totalCount,
                        rowsPerPage: rowsPerPage,
                        page: page,
                        onPageChange,
                        onRowsPerPageChange,
                        ...getPaginationLabels(t),
                        pages,
                    }}
                />
            </TableContainer>
            {isAddLocationOpen && (
                <AddLocationModal
                    isOpen={isAddLocationOpen}
                    onClose={() => setIsAddLocationOpen(false)}
                    setSelectedCompany={setSelectedCompany}
                    changeView={() => setView(view.FORM)}
                />
            )}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => {
                        setDeleteModalOpen(false);
                        setSelectedCompany({});
                    }}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
            {isUpdateLocationsOpen && (
                <UpdateLocations open={isUpdateLocationsOpen} setOpen={setIsUpdateLocationsOpen} />
            )}
            {isBulkEditOpen && (
                <BulkEditLocations
                    open={isBulkEditOpen}
                    onClose={() => setIsBulkEditOpen(false)}
                    onUpdated={() => {
                        dispatch(accountActions.getLocations(filter, page, rowsPerPage));
                        dispatch(accountActions.get());
                    }}
                />
            )}
            {isImportGoogleCompaniesOpen ? (
                <ImportGoogleCompanies
                    isOpen={isImportGoogleCompaniesOpen}
                    onClose={() => setIsImportGoogleCompaniesOpen(false)}
                />
            ) : null}
        </PageContainer>
    );
};

export default CompaniesTable;
